import {
  // AttachmentMeta,
  // FileAttachmentEntity,
  LinkEntity,
  // MediaAttachmentEntity,
  MessageContentEntity,
  MessageEntity,
  MessageSendingStatus,
  ReactionSendingStatus,
} from "@/indexedDB/types";
import {
  decryptMessageContent,
  // decryptServiceMessageContent,
  // encryptMessageAttachment,
  // encryptMessageAttachmentMeta,
  // encryptMessageContent,
  umbra_decryptServiceMessageContent,
} from "./crypto";
import { isDecryptionError, tryDecrypt } from "./decryptionError";
import {
  addMessagesToLocalDB,
  getMessageByServerIdFromLocalDB,
  // addMessageToLocalDB,
  // getMessageByIdFromLocalDB,
  getMessageCountByChatIdFromLocalDB,
  // removeMessageByIdFromLocalDB,
  // updateMessageInLocalDB,
} from "@/indexedDB/services/messages";
import { isUserAuthenticated } from "./auth";
import { processInlineEpochPayloads } from "./epochKeyCache";

import {
  // EncryptedMessage,
  MessageCategory,
  // MessageType,
} from "@/server/types/message";
import {
  getChatFromLocalDB,
  updateChatInLocalDB,
} from "@/indexedDB/services/chats";
import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
import { isCiphertext } from "@/lib/dataConversions";
import { useNavigationState } from "@/zustand/NavigationState";
import {
  extractTimestampFromUUID7,
  generateUuid7,
  // generateUuid7
} from "@/utils/uuid7";

import { processMessageLink } from "./links";

import {
  addLinksToLocalDB,
  // addLinkToLocalDB,
  // deleteLinkByIdFromLocalDB,
} from "@/indexedDB/services/links";

import {
  umbra_getMessage,
  umbra_getMessages,
} from "@/server/services/umbra/messageService";
import { EncryptedMessage } from "@/server/types/umbra/message";
import { deriveCommentCount } from "@/indexedDB/services/syncMappers";
import { validateMessageIds } from "./messages";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";
import { getPinnedMessageByServerIdFromLocalDB } from "@/indexedDB/services/pinnedMessages";
import { featureFlags } from "@/config/featureFlags";
import { reconcilePinnedMessages } from "./pinnedReconcile";
import { createLogger } from "@/utils/debug";

const log = createLogger("UmbraMessages");

export const umbra_processParentMessage = async ({
  messageId,
  chatId,
  eCSK,
  signal,
}: {
  messageId: string;
  chatId: string;
  eCSK: string;
  signal?: AbortSignal;
}): Promise<MessageEntity | undefined> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    // TO DO => Check the implementation of "ForcedReconciliation" using this func
    // => if we check for existing msg in indexed db => Reactions workflow is broken
    // => reactions remain in pending state forever

    // check if message already processed in local indexed DB
    const messageInIndexedDB = await getMessageByServerIdFromLocalDB(messageId);
    if (messageInIndexedDB) {
      return messageInIndexedDB;
    }

    let msgContent: MessageContentEntity;
    const reactions: Record<string, number> = {};

    const eMessage = await umbra_getMessage({ chatId, messageId, signal });

    if (!eMessage) {
      return undefined;
    }

    const isReply = !!eMessage.parent_id;
    let decryptionFailed = false;
    let attemptedDecrypt = false;

    // decrypt  message content
    // TO DO => THIS IS TEMPORARY (Extract to better msg processing)
    if (eMessage.category === MessageCategory.DELETED) {
      return undefined;
    }

    if (!eCSK) {
      decryptionFailed = true;
      msgContent = { text: "" };
    } else {
      attemptedDecrypt = true;
      const decrypted = await tryDecrypt(() =>
        decryptMessageContent(
          chatId,
          eMessage.key_epoch ?? 0,
          eCSK,
          eMessage.content,
          eMessage.attachment_meta,
        ),
      );
      if (decrypted.ok) {
        msgContent = decrypted.data;
      } else {
        decryptionFailed = true;
        msgContent = { text: "" };
      }

      // process reactions — skip undecryptable ones rather than failing the
      // entire parent message just because one reaction can't be decrypted.
      for (const [content, count] of Object.entries(eMessage.reactions)) {
        const isEncrypted = isCiphertext(content);
        if (isEncrypted) {
          try {
            const decryptedReaction = await decryptMessageContent(
              chatId,
              eMessage.key_epoch ?? 0,
              eCSK,
              content,
            );
            reactions[decryptedReaction.text] = count;
          } catch (error) {
            if (!isDecryptionError(error)) {
              throw error;
            }
            // Skip undecryptable reaction.
          }
        } else {
          reactions[content] = count;
        }
      }
    }

    const isPinnedMessage =
      await getPinnedMessageByServerIdFromLocalDB(messageId);

    const msg: MessageEntity = {
      _clientId: eMessage.client_id || eMessage.id, // Use server message ID as client ID if client_id is not present
      id: eMessage.id,
      chatId: eMessage.chat_id,
      sender: eMessage.sender,
      avatarId: eMessage.sender_avatar,
      timestamp: extractTimestampFromUUID7(eMessage.id),
      category: eMessage.category,
      content: msgContent,
      hasAttachment: !!eMessage.attachment_meta,
      isEdited: eMessage.is_edited,
      isOutgoing: userFingerPrint.username === eMessage.sender,
      // comment related
      commentCount: deriveCommentCount(eMessage),
      senderAvatar: eMessage.sender_avatar,
      parentCommentCount: eMessage.parent_comment_count,
      // comment thread
      threadId: eMessage.thread_id,
      commentSentAt: null, // No longer needed with UUID7
      isReply,
      isPinned: !!isPinnedMessage,
      parentMessage: isReply
        ? {
            id: eMessage.parent_id,
            category: eMessage.parent_category,
          }
        : undefined,
      tags: eMessage.tags?.length ? eMessage.tags : undefined,
      sendingStatus: MessageSendingStatus.DELIVERED,
      reactions,
      userReaction:
        eMessage.user_reactions.length > 0
          ? {
              content: eMessage.user_reactions[0],
              messageId: eMessage.id,
              sendingStatus: ReactionSendingStatus.SUCCESS,
            }
          : undefined,
      ...(decryptionFailed
        ? {
            decryptionStatus: "failed" as const,
            ...(attemptedDecrypt ? { decryptionAttempts: 1 } : {}),
            // Retain the ciphertext (v14 sidecar) so retry decrypts from LOCAL
            // bytes, never a per-message GET — no re-mint of the legacy class.
            ciphertext: {
              content: eMessage.content,
              attachmentMeta: eMessage.attachment_meta ?? null,
            },
          }
        : { decryptionStatus: "ok" as const }),
      keyEpoch: eMessage.key_epoch ?? null,
    };

    return msg;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return undefined;
  }
};

export const umbra_processMessageContent = async (
  chatId: string,
  eCSK: string,
  eMessages: EncryptedMessage[],
): Promise<MessageEntity[]> => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  const output: MessageEntity[] = [];

  for (const msg of eMessages) {
    let msgContent: MessageContentEntity;
    const reactions: Record<string, number> = {};
    const isReply = !!msg.parent_id;
    let decryptionFailed = false;
    // True only when we actually invoked a decryptor. Missing eCSK is
    // transient and shouldn't burn a retry-budget attempt.
    let attemptedDecrypt = false;

    // decrypt  message content
    // TO DO => THIS IS TEMPORARY (Extract to better msg processing)
    try {
      if (msg.category === MessageCategory.SERVICE) {
        attemptedDecrypt = true;
        msgContent = await umbra_decryptServiceMessageContent(msg);
      } else if (!eCSK) {
        // Transient: chat row exists but secure_payload not synced yet.
        decryptionFailed = true;
        msgContent = { text: "" };
      } else {
        attemptedDecrypt = true;
        msgContent = await decryptMessageContent(
          chatId,
          msg.key_epoch ?? 0,
          eCSK,
          msg.content,
          msg.attachment_meta,
        );

        // TO DO => check if we still need this after umbra_decryptServiceMessageContent update
        // process reactions
        if (msg.reactions) {
          for (const [content, count] of Object.entries(msg.reactions)) {
            const isEncrypted = isCiphertext(content);
            if (isEncrypted) {
              try {
                const decryptedReaction = await decryptMessageContent(
                  chatId,
                  msg.key_epoch ?? 0,
                  eCSK,
                  content,
                );
                reactions[decryptedReaction.text] = count;
              } catch (error) {
                if (!isDecryptionError(error)) {
                  throw error;
                }
                // Skip undecryptable reactions; the message itself can still render.
              }
            } else {
              reactions[content] = count;
            }
          }
        }
      }
    } catch (error) {
      if (!isDecryptionError(error)) {
        throw error;
      }
      decryptionFailed = true;
      msgContent = { text: "" };
    }

    const isPinnedMessage = await getPinnedMessageByServerIdFromLocalDB(msg.id);

    const _msg: MessageEntity = {
      _clientId: msg.client_id || msg.id, // Use client_id if present, otherwise use server id
      id: msg.id,
      chatId: msg.chat_id,
      sender: msg.sender,
      // Read-time anonymisation marker — present only when true.
      ...(msg.sender_deleted ? { senderDeleted: true } : {}),
      avatarId: msg.sender_avatar || "",
      timestamp: extractTimestampFromUUID7(msg.id),
      category: msg.category,
      content: msgContent,
      hasAttachment: !!msg.attachment_meta,
      isEdited: msg.is_edited,
      isOutgoing: userFingerPrint.username === msg.sender,
      // comment related
      commentCount: deriveCommentCount(msg),
      senderAvatar: msg.sender_avatar || "",
      parentCommentCount: null,
      // comment thread
      threadId: msg.thread_id,
      commentSentAt: null, // No longer needed with UUID7
      isReply,
      isPinned: !!isPinnedMessage,
      parentMessage: isReply
        ? {
            id: msg.parent_id,
            category: msg.parent_category,
          }
        : undefined,
      tags: msg.tags?.length ? msg.tags : undefined,
      sendingStatus: MessageSendingStatus.READ,
      reactions,
      userReaction:
        msg.user_reactions.length > 0
          ? {
              content: msg.user_reactions[0],
              messageId: msg.id,
              sendingStatus: ReactionSendingStatus.SUCCESS,
            }
          : undefined,
      ...(decryptionFailed
        ? {
            decryptionStatus: "failed" as const,
            // Only burn an attempt when we actually invoked a decryptor.
            ...(attemptedDecrypt ? { decryptionAttempts: 1 } : {}),
            // Retain the ciphertext (v14 sidecar) so the retry orchestrator /
            // lazy-decrypt re-attempts from LOCAL bytes — NEVER a per-message
            // GET. Source fix for the refetch storm: a re-paged row that still
            // can't decrypt no longer re-mints the ciphertext-less legacy class
            // (mirrors syncBackfillDecrypt's fallback).
            ciphertext: {
              content: msg.content,
              attachmentMeta: msg.attachment_meta ?? null,
            },
          }
        : { decryptionStatus: "ok" as const }),
      keyEpoch: msg.key_epoch ?? null,
    };

    output.push(_msg);
  }

  // Validate that all message IDs are proper UUID7 format
  const validatedOutput = validateMessageIds(output);

  return validatedOutput;
};

export const umbra_processMessages = async ({
  chatId,
  eCSK,
  after,
  before,
  pageSize,
  signal,
}: {
  chatId: string;
  eCSK: string;
  after?: string;
  before?: string;
  pageSize?: number;

  signal?: AbortSignal;
}): Promise<MessageEntity[]> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return [];
  }
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // check if chat exists in local indexed DB
  const localDbChat = await getChatFromLocalDB(chatId);
  if (!localDbChat) {
    throw new Error("Chat not found in local DB");
  }

  // get user role in chat
  const userRoleInChat = localDbChat.roleAsMember.role;

  try {
    let output: MessageEntity[] = [];

    const includeSystemMessages =
      localDbChat.type === ChatType.GROUP &&
      userRoleInChat !== MemberRole.MEMBER;

    const umbra_messages = await umbra_getMessages({
      chatId,
      after,
      before,
      pageSize,
      includeSystem: includeSystemMessages,
      signal,
    });

    const eMessages = umbra_messages.messages;

    // Batch self-encapsulate + store any epoch_payloads from the response
    await processInlineEpochPayloads(chatId, umbra_messages.epoch_payloads);

    if (!eMessages.length) {
      return output;
    }

    output = await umbra_processMessageContent(chatId, eCSK, eMessages);

    return output;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return [];
  }
};

export const umbra_progressivelyLoadMessages = async (
  chatId: string,
  max?: number,
  signal?: AbortSignal,
) => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // Check for user authentication.
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // Global state references
  const setActiveChatInitialMessagesLoaded =
    useNavigationState.getState().setActiveChatInitialMessagesLoaded;
  const setActiveChatFullMessageListLoaded =
    useNavigationState.getState().setActiveChatFullMessageListLoaded;
  const setActiveChatBatchCount =
    useNavigationState.getState().setActiveChatBatchCount;
  const setActiveChatRenderedBatchCount =
    useNavigationState.getState().setActiveChatRenderedBatchCount;

  try {
    const localDbChat = await getChatFromLocalDB(chatId);

    if (!localDbChat) {
      return;
    }
    // TO DO => implement caching with TTL on CSKs
    const eCSK = localDbChat.eCSK;

    // New-sync parity: the sync engine never writes the `pinnedMessages` table
    // (it carries `chat.pinnedMessageIds` instead), so after logout/login the
    // pinned panel would be empty. Reconcile it from the chat row BEFORE any of
    // the early-returns below (full-list-init, empty/short loads) so it works
    // for already-loaded chats, empty chats, and when pinnedMessageIds changed.
    // Idempotent + cheap (no-op when the local set already matches). Legacy
    // path is untouched — gated on the flag. Fire-and-forget so it never blocks
    // (or fails) the message load.
    if (featureFlags.useNewSync) {
      void reconcilePinnedMessages(localDbChat, signal).catch(() => {
        // Best-effort: a pinned-reconcile failure must not break message loading.
      });
    }

    // if all msg are loaded return // TO DO => move to local store for faster access
    if (localDbChat.fullMsgListInit) {
      // Ensure loading flag is set even on early return — covers cases where
      // persisted state or setActiveChatId had a stale value
      const activeChatId = useNavigationState.getState().activeChatId;
      if (chatId === activeChatId) {
        setActiveChatInitialMessagesLoaded(true);
        setActiveChatFullMessageListLoaded(true);
      }
      return;
    }

    // if first phase not completed
    if (!localDbChat.firstPhaseMsgListInit) {
      // Fetch a batch starting at the current offset.
      const firstBatch = await umbra_processMessages({
        chatId,
        eCSK,
        before: generateUuid7(),
        pageSize: MESSAGE_LIST_PAGE_SIZE,
        signal,
      });

      if (signal?.aborted) {
        return;
      }

      if (firstBatch.length > 0) {
        // add messages to local db
        await addMessagesToLocalDB(firstBatch);
        // Mark the first loading phase as complete.
        await updateChatInLocalDB(chatId, {
          firstMessageId: firstBatch[firstBatch.length - 1].id,
          firstPhaseMsgListInit: true,
          // lastSyncedAt: new Date().toISOString(),
          lastSyncedAt: extractTimestampFromUUID7(firstBatch[0].id), // set to latest msg timestamp in batch
          batchCount: 1, // first batch
        });

        // if chat id is active
        const activeChatId = useNavigationState.getState().activeChatId;
        if (chatId === activeChatId) {
          await updateChatInLocalDB(chatId, {
            renderedBatchCount: 1, // first rendered batch
          });

          setActiveChatInitialMessagesLoaded(true);
          setActiveChatBatchCount(1); // first batch
          setActiveChatRenderedBatchCount(1); // first rendered batch
        }

        // process parent msg if replies found
        const parentMsgs: MessageEntity[] = [];
        // process links with no metadata
        const links: LinkEntity[] = [];

        for (const msg of firstBatch) {
          // TO DO => check if parent msg already processed - to avoid extra request
          if (msg.isReply) {
            // check if parent msg is already processed in this batch
            const parentIndex = firstBatch.findIndex(
              (fbMsg) =>
                fbMsg.id === msg.parentMessage?.id &&
                fbMsg.timestamp === msg.parentMessage?.timestamp,
            );

            // if parent msg not found in batch
            if (parentIndex === -1) {
              const parentMsg = await umbra_processParentMessage({
                messageId: msg.parentMessage?.id || "",
                // category: msg.parentMessage?.category || 0,
                chatId,
                eCSK,
              });
              if (parentMsg) {
                parentMsgs.push(parentMsg);
              }
            }
          }

          if (msg.content.link) {
            const linkData = await processMessageLink({
              messageId: msg.id,
              chatId,
              timestamp: msg.timestamp,
              link: msg.content.link,
              withMetadata: true,
              signal,
            });

            if (linkData) {
              links.push(linkData);
            }
          }
        }

        if (parentMsgs.length > 0) {
          // Best-effort secondary write (reply-parent context). A bulkPut reject
          // — e.g. the Safari "without an in-progress transaction" IndexedDB
          // error — must NOT crash the loader or escape as an unhandled
          // rejection: the primary message batch is already awaited + rendered.
          void addMessagesToLocalDB(parentMsgs).catch((error) => {
            log.error("Failed to persist parent messages", error);
          });
        }
        if (links.length > 0) {
          void addLinksToLocalDB(links).catch((error) => {
            log.error("Failed to persist links", error);
          });
        }

        // if first batch is less than page size, mark the whole list as complete and exit
        if (firstBatch.length < MESSAGE_LIST_PAGE_SIZE) {
          await updateChatInLocalDB(chatId, {
            fullMsgListInit: true,
            // lastSyncedAt: new Date().toISOString(),
          });

          // if chat id is active
          if (chatId === activeChatId) {
            setActiveChatFullMessageListLoaded(true);
          }

          return;
        }
      } else {
        // if no msg in first batch mark everything as complete
        await updateChatInLocalDB(chatId, {
          firstPhaseMsgListInit: true,
          fullMsgListInit: true,
          lastSyncedAt: new Date().toISOString(),
          // lastSyncedAt: extractTimestampFromUUID7(firstBatch[0].id), // set to latest msg timestamp in batch
        });

        // if chat id is active
        const activeChatId = useNavigationState.getState().activeChatId;
        if (chatId === activeChatId) {
          setActiveChatFullMessageListLoaded(true);
          setActiveChatInitialMessagesLoaded(true);
        }

        return;
      }
    }

    let moreMessages: MessageEntity[] = [];
    let currentMsgCount: number;

    do {
      // extra check on abort controller
      if (signal?.aborted) {
        return;
      }

      const _localDbChat = await getChatFromLocalDB(chatId);
      if (!_localDbChat) {
        return;
      }

      moreMessages = await umbra_processMessages({
        chatId,
        eCSK,
        before: _localDbChat.firstMessageId,
        pageSize: MESSAGE_LIST_PAGE_SIZE,
        signal,
      });

      // Exit loop if no messages are returned
      if (!moreMessages.length) {
        break;
      }

      await updateChatInLocalDB(chatId, {
        firstMessageId: moreMessages[moreMessages.length - 1].id,
        // lastSyncedAt: new Date().toISOString(),
        lastSyncedAt: extractTimestampFromUUID7(moreMessages[0].id), // set to latest msg timestamp in batch
        batchCount: (_localDbChat.batchCount || 0) + 1, // increment batch count
      });

      const activeChatId = useNavigationState.getState().activeChatId;
      if (chatId === activeChatId) {
        setActiveChatBatchCount((_localDbChat.batchCount || 0) + 1); // increment batch count
      }

      // add messages to local db
      await addMessagesToLocalDB(moreMessages);

      // process links with no metadata
      const links: LinkEntity[] = [];
      // process parent msg if replies found
      const parentMsgs: MessageEntity[] = [];

      for (const msg of moreMessages) {
        // TO DO => check if parent msg already processed - to avoid extra request
        if (msg.isReply) {
          // check if parent msg is already processed in this batch
          const parentIndex = moreMessages.findIndex(
            (fbMsg) =>
              fbMsg.id === msg.parentMessage?.id &&
              fbMsg.timestamp === msg.parentMessage?.timestamp, //
          );

          // if parent msg not found in batch
          if (parentIndex === -1) {
            const parentMsg = await umbra_processParentMessage({
              messageId: msg.parentMessage?.id || "",
              // category: msg.parentMessage?.category || 0,
              chatId,
              eCSK,
            });
            if (parentMsg) {
              parentMsgs.push(parentMsg);
            }
          }
        }

        if (msg.content.link) {
          const linkData = await processMessageLink({
            messageId: msg.id,
            chatId,
            timestamp: msg.timestamp,
            link: msg.content.link,
            withMetadata: true,
            signal,
          });

          if (linkData) {
            links.push(linkData);
          }
        }
      }

      // Persist accumulated reply-parents + links ONCE after the batch loop —
      // NOT per-iteration (that re-wrote the growing arrays on every message,
      // multiplying IndexedDB write pressure — the very contention this guard is
      // mitigating). Best-effort secondary writes: a bulkPut reject (e.g. the
      // Safari "without an in-progress transaction" IndexedDB error) must NOT
      // crash the loader or escape as an unhandled rejection — the primary batch
      // is already awaited + rendered.
      if (parentMsgs.length > 0) {
        void addMessagesToLocalDB(parentMsgs).catch((error) => {
          log.error("Failed to persist parent messages", error);
        });
      }
      if (links.length > 0) {
        void addLinksToLocalDB(links).catch((error) => {
          log.error("Failed to persist links", error);
        });
      }

      // if result is less than MESSAGE_LIST_PAGE_SIZE during loop - exit
      if (moreMessages.length < MESSAGE_LIST_PAGE_SIZE) {
        //  Finalize the state by marking progressive init as finished.
        await updateChatInLocalDB(chatId, {
          fullMsgListInit: true,
        });
        // if chat id is active
        const _activeChatId = useNavigationState.getState().activeChatId;
        if (chatId === _activeChatId) {
          setActiveChatFullMessageListLoaded(true);
        }
        break;
      }

      currentMsgCount = await getMessageCountByChatIdFromLocalDB(chatId);
    } while (moreMessages.length > 0 && currentMsgCount <= (max || 5000));

    // TODO: Discuss with Andrei:
    //  - why updating lastSyncedAt here?

    // Because  -> //  Finalize the loop by setting lastSyncedAt at the time of finishing the loop.
    // Also  -> //  Finalize the loop by marking progressive init as finished in local state.

    await updateChatInLocalDB(chatId, {
      fullMsgListInit: true,
      // lastSyncedAt: new Date().toISOString(),
    });
    // if chat id is active
    const activeChatId = useNavigationState.getState().activeChatId;
    if (chatId === activeChatId) {
      setActiveChatFullMessageListLoaded(true);
    }
  } catch (error) {
    // On error, still mark initial messages as loaded so the UI doesn't stay
    // permanently stuck on "Loading conversation history..."
    const activeChatId = useNavigationState.getState().activeChatId;
    if (chatId === activeChatId) {
      setActiveChatInitialMessagesLoaded(true);
    }

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot load message list.");
  }
};
